<section class="dashboard">
  <div class="dashboard-main">
    <a class="back" routerLink="/orders">‹ All orders</a>

    @if (loading()) {
      <p class="muted">Loading…</p>
    } @else if (!order()) {
      <div class="empty">Order not found.</div>
    } @else if (order(); as o) {
      <header class="head">
        <div>
          <h1>{{ o.orderNumber ?? o.id }}</h1>
          <p class="muted">Placed {{ o.createdAt | date: "medium" }}</p>
        </div>
        <div class="head-badges">
          <span class="pay-badge" [class.paid]="o.paymentStatus === 'paid'">
            {{ o.paymentStatus === "paid" ? "Paid" : "Payment pending" }}
          </span>
          @if (!editing()) {
            <button class="btn-primary" type="button" (click)="startEdit()">
              Edit order
            </button>
            @if (saved()) {
              <span class="saved">Saved.</span>
            }
          } @else {
            <button class="btn-primary" type="button" (click)="save()" [disabled]="busy()">
              {{ busy() ? "Saving…" : "Save changes" }}
            </button>
            <button class="btn-secondary" type="button" (click)="cancelEdit()" [disabled]="busy()">
              Cancel
            </button>
          }
        </div>
      </header>

      @if (notice(); as n) {
        <div
          class="notice"
          [class.error]="n.kind === 'error'"
          role="status"
        >
          {{ n.text }}
        </div>
      }

      <nav class="tab-nav" role="tablist" aria-label="Order workspace">
        <button
          type="button"
          role="tab"
          class="tab-button"
          [class.active]="activeTab() === 'actions'"
          [attr.aria-selected]="activeTab() === 'actions'"
          (click)="setTab('actions')"
        >
          Actions
        </button>
        <button
          type="button"
          role="tab"
          class="tab-button"
          [class.active]="activeTab() === 'measurements'"
          [attr.aria-selected]="activeTab() === 'measurements'"
          (click)="setTab('measurements')"
        >
          Measurements
        </button>
      </nav>

      @if (activeTab() === "measurements") {
        @if (draft(); as d) {
          <div class="panel">
            <h2>Measurements</h2>
            <div class="table-wrap">
              <table class="items-table">
                <thead>
                  <tr>
                    <th>Room</th>
                    <th>Label</th>
                    <th>Style</th>
                    <th>Control</th>
                    <th>Shading</th>
                    <th class="num">W</th>
                    <th class="num">H</th>
                    <th class="num">Price</th>
                    <th>Solar</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  @for (item of d.items; track $index) {
                    <tr>
                      <td><input class="cell" [(ngModel)]="item.roomName" [disabled]="!editing()" /></td>
                      <td><input class="cell" [(ngModel)]="item.label" [disabled]="!editing()" /></td>
                      <td><input class="cell" [(ngModel)]="item.blindCode" [disabled]="!editing()" /></td>
                      <td>
                        @if (item.isRemote) {
                          <span class="muted">—</span>
                        } @else {
                          <select [(ngModel)]="item.controlType" [disabled]="!editing()">
                            <option value="cordless">Cordless</option>
                            <option value="motorized">A-OK Matter motorized</option>
                          </select>
                        }
                      </td>
                      <td>
                        @if (item.isRemote) {
                          <span class="muted">—</span>
                        } @else {
                          <select [(ngModel)]="item.opacity" [disabled]="!editing()">
                            <option value="semi-blackout">Semi blackout</option>
                            <option value="blackout">Blackout</option>
                          </select>
                        }
                      </td>
                      <td class="num">
                        @if (item.isRemote) {
                          <span class="muted">—</span>
                        } @else {
                          <input class="cell num" type="number" [(ngModel)]="item.width" [disabled]="!editing()" />
                        }
                      </td>
                      <td class="num">
                        @if (item.isRemote) {
                          <span class="muted">—</span>
                        } @else {
                          <input class="cell num" type="number" [(ngModel)]="item.height" [disabled]="!editing()" />
                        }
                      </td>
                      <td class="num"><input class="cell num" type="number" [(ngModel)]="item.price" [disabled]="!editing()" /></td>
                      <td class="center">
                        @if (!item.isRemote) {
                          <input type="checkbox" [(ngModel)]="item.hasSolarPanel" [disabled]="!editing()" />
                        }
                      </td>
                      <td>
                        <button class="btn-mini" type="button" (click)="removeItem($index)" [disabled]="!editing()">✕</button>
                      </td>
                    </tr>
                  }
                </tbody>
              </table>
            </div>
            @if (editing()) {
              <button class="btn-secondary" type="button" (click)="addItem()">+ Add item</button>
            }

            <div class="totals">
              <div class="line"><span>Subtotal</span><span>{{ money(subtotal()) }}</span></div>
              <div class="line">
                <span>Tax</span>
                <input class="cell num tax" type="number" [(ngModel)]="d.tax" [disabled]="!editing()" />
              </div>
              <div class="line strong"><span>Total</span><span>{{ money(total()) }}</span></div>
            </div>
          </div>
        }
      } @else {
        <div class="panel progress-accent">
          <h2>Status</h2>
          <ol class="status-stepper">
            @for (s of statusFlow; track s; let i = $index) {
              <li class="step" [ngClass]="'step-' + statusStepState(i)">
                <button
                  type="button"
                  class="step-hit"
                  (click)="setStatus(s)"
                  [disabled]="busy()"
                  [attr.aria-current]="o.status === s"
                >
                  <span class="step-dot" aria-hidden="true"></span>
                  <span class="step-label">{{ statusLabel(s) }}</span>
                </button>
              </li>
            }
          </ol>
        </div>

        @if (draft(); as d) {
          <div class="panel">
            <h2>Customer</h2>
            <div class="field-grid">
              <label>Name<input [(ngModel)]="d.customerName" [disabled]="!editing()" /></label>
              <label>Email<input type="email" [(ngModel)]="d.customerEmail" [disabled]="!editing()" /></label>
              <label>Phone<input [(ngModel)]="d.address.phone" [disabled]="!editing()" /></label>
            </div>
          </div>

          <div class="panel">
            <h2>Ship to</h2>
            <div class="field-grid">
              <label>Name<input [(ngModel)]="d.address.name" [disabled]="!editing()" /></label>
              <label>Line 1<input [(ngModel)]="d.address.line1" [disabled]="!editing()" /></label>
              <label>Line 2<input [(ngModel)]="d.address.line2" [disabled]="!editing()" /></label>
              <label>City<input [(ngModel)]="d.address.city" [disabled]="!editing()" /></label>
              <label>State<input [(ngModel)]="d.address.state" [disabled]="!editing()" /></label>
              <label>ZIP<input [(ngModel)]="d.address.zip" [disabled]="!editing()" /></label>
            </div>
          </div>

          <div class="panel">
            <h2>Fulfilment</h2>
            <div class="field-grid">
              <label>SF Express waybill<input [(ngModel)]="d.trackingNumber" [disabled]="!editing()" /></label>
            </div>
          </div>
        }

        <div class="panel">
          <h2>Payment</h2>
          @if (o.payment; as p) {
            <div class="kv"><span>Provider</span><span>{{ p.provider === "stripe" ? "Stripe" : "Mock (no charge)" }} · {{ p.status }}</span></div>
            <div class="kv"><span>Amount</span><span>{{ money(p.amount) }} {{ p.currency.toUpperCase() }}</span></div>
            @if (p.cardBrand) {
              <div class="kv"><span>Card</span><span>{{ p.cardBrand }} •••• {{ p.cardLast4 }}</span></div>
            }
            @if (p.paidAt) {
              <div class="kv"><span>Paid</span><span>{{ p.paidAt | date: "medium" }}</span></div>
            }
            @if (p.livemode === false) {
              <div class="kv"><span>Mode</span><span class="test-badge">TEST MODE</span></div>
            }
            @if (p.paymentIntentId) {
              <div class="kv"><span>PaymentIntent</span><span>{{ p.paymentIntentId }}</span></div>
            }
            <div class="links">
              @if (p.receiptUrl) {
                <a [href]="p.receiptUrl" target="_blank" rel="noopener">Receipt</a>
              }
              @if (p.paymentIntentId) {
                <a [href]="stripeUrl(p)" target="_blank" rel="noopener">Open in Stripe</a>
              }
            </div>
          } @else {
            <p class="muted">No payment record.</p>
          }
        </div>

        <div class="panel">
          <h2>Suppliers</h2>
          @if (suppliers().length === 0) {
            <p class="muted">No supplier is linked to these blinds.</p>
            <p class="muted">Set a supplier on the blind styles to route this order.</p>
          } @else {
            <div class="supplier-cards">
              @for (sid of suppliers(); track sid) {
                <div class="supplier-card" [ngClass]="'stage-' + stageFor(sid)">
                  <div class="supplier-card-head">
                    <span class="supplier-name">{{ supplierName(sid) }}</span>
                    <span class="supplier-pill" [ngClass]="'pill-' + stageFor(sid)">
                      {{ stageLabel(stageFor(sid)) }}
                    </span>
                  </div>

                  <ol class="supplier-stepper">
                    @for (step of stepsFor(sid); track step.label) {
                      <li class="step" [ngClass]="'step-' + step.state">
                        <span class="step-dot" aria-hidden="true"></span>
                        <span class="step-label">{{ step.label }}</span>
                      </li>
                    }
                  </ol>

                  @if (scopeText(sid); as scope) {
                    <div class="supplier-scope">{{ scope }}</div>
                  }

                  <div class="supplier-foot">
                        <span class="supplier-amount">
                          @if (quoteFor(sid); as q) {
                            @if (q.amount != null) {
                              {{ money(q.amount) }}
                            }
                          } @else if (stageFor(sid) === "awaiting") {
                            <span class="muted">Waiting on supplier</span>
                          }
                        </span>

                    <span class="supplier-actions">
                      @if (stageFor(sid) === "awaiting") {
                        @if (supplierEmail(sid); as email) {
                          <a class="link-action" [href]="'mailto:' + email">Email supplier</a>
                        }
                      }
                      @if (stageFor(sid) === "received") {
                        <button class="btn-mini paid" type="button" (click)="approveQuote(sid)" [disabled]="busy()">
                          Approve quote
                        </button>
                        <button class="btn-mini" type="button" (click)="rejectQuote(sid)" [disabled]="busy()">
                          Reject
                        </button>
                      }
                      @if (stageFor(sid) === "approved") {
                        @if (alibabaUrl(sid); as alibaba) {
                          <a class="alibaba-pay" [href]="alibaba" target="_blank" rel="noopener">
                            Pay on Alibaba →
                          </a>
                        }
                        <button class="btn-mini paid" type="button" (click)="markPaid(sid)" [disabled]="busy()">
                          Mark paid
                        </button>
                      }
                    </span>
                  </div>

                  @if (quoteUrl(sid) || trackUrl()) {
                    <div class="supplier-docs">
                      @if (quoteUrl(sid); as url) {
                        <a class="doc-button" [href]="url" target="_blank" rel="noopener">
                          <svg
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            stroke-width="2"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            aria-hidden="true"
                          >
                            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                            <polyline points="7 10 12 15 17 10" />
                            <line x1="12" y1="15" x2="12" y2="3" />
                          </svg>
                          Download supplier quote file
                        </a>
                      }
                      @if (trackUrl(); as url) {
                        <a class="doc-button" [href]="url" target="_blank" rel="noopener">
                          <svg
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            stroke-width="2"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            aria-hidden="true"
                          >
                            <rect x="1" y="3" width="15" height="13" />
                            <polygon points="16 8 20 8 23 11 23 16 16 16 16 8" />
                            <circle cx="5.5" cy="18.5" r="2.5" />
                            <circle cx="18.5" cy="18.5" r="2.5" />
                          </svg>
                          Track the item
                        </a>
                      }
                    </div>
                  }
                </div>
              }
            </div>
          }
        </div>
      }
    }
  </div>
</section>
